import { useRef, useState } from "react";
import { Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { AttachFile, Close } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { useSnackbar } from "notistack";
import { nanoid } from "nanoid";
import { useQueryClient } from "@/compat/reactQuery";
import queryKeys from "../../../constants/queryKeys";
import { acceptedAttachmentsAttribute } from "../../constants/attachmentFileTypes";
import { attachmentUploadError } from "../../utils/attachmentErrors";
import riskAssessmentAttachmentService from "../../services/riskAssessmentAttachmentService";
import useRiskAssessmentAttachments from "../../hooks/queries/useRiskAssessmentAttachments";
import useUploadRiskAssessmentAttachments from "../../hooks/mutations/useUploadRiskAssessmentAttachments";
import useDeleteRiskAssessmentAttachment from "../../hooks/mutations/useDeleteRiskAssessmentAttachment";
import type { RiskAssessmentAttachmentDTO } from "../../types/riskAssessmentAttachmentDtos";

const useStyles = makeStyles((theme) => ({
    row: { alignItems: "center", display: "flex", gap: theme.spacing(0.5) },
    name: { maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    hidden: { display: "none" },
}));

interface HazardAttachmentsProps {
    riskAssessmentId: number;
    azureFileIds: number[];
    onChange: (azureFileIds: number[]) => void;
    disabled?: boolean;
}

// Files upload straight away, against the assessment — the hazard row they belong
// to doesn't exist until the assessment is submitted, which is when they're pinned.
export default function HazardAttachments({
    riskAssessmentId,
    azureFileIds,
    onChange,
    disabled = false,
}: HazardAttachmentsProps) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const fileInput = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);

    const attachments = useRiskAssessmentAttachments(riskAssessmentId);
    const upload = useUploadRiskAssessmentAttachments();
    const remove = useDeleteRiskAssessmentAttachment();

    const all: RiskAssessmentAttachmentDTO[] = Array.isArray(attachments?.data) ? attachments.data : [];
    const mine = all.filter((x) => azureFileIds.includes(x.azureFileId));

    const refresh = () =>
        queryClient.invalidateQueries({ queryKey: [queryKeys.riskAssessmentAttachments, riskAssessmentId] });

    const onFilesPicked = (fileList?: FileList | null) => {
        if (!fileList?.length) return;

        setBusy(true);

        upload.mutate(
            { riskAssessmentId, files: [...fileList].map((file) => ({ syncId: nanoid(5), file })) },
            {
                onSuccess: (uploaded: any) => {
                    setBusy(false);
                    if (fileInput.current) fileInput.current.value = "";
                    onChange([...azureFileIds, ...(uploaded ?? []).map((x: any) => x.azureFileId)]);
                    refresh();
                },
                onError: (error: any) => {
                    setBusy(false);
                    enqueueSnackbar(attachmentUploadError(error), { variant: "error" });
                },
            }
        );
    };

    const removeFile = (attachment: RiskAssessmentAttachmentDTO) =>
        remove.mutate(attachment.id, {
            onSuccess: () => {
                onChange(azureFileIds.filter((id) => id !== attachment.azureFileId));
                refresh();
            },
            onError: () => enqueueSnackbar("Could not remove the file. Please try again.", { variant: "error" }),
        });

    return (
        <div>
            {mine.map((attachment) => (
                <div key={attachment.id} className={classes.row}>
                    <Typography variant="caption" className={classes.name} title={attachment.fileName}>
                        {attachment.fileName}
                    </Typography>
                    {!disabled && (
                        <Tooltip title="Remove">
                            <IconButton size="small" onClick={() => removeFile(attachment)}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ))}

            {!disabled && (
                <Button
                    size="small"
                    color="primary"
                    startIcon={busy ? <CircularProgress size={14} /> : <AttachFile />}
                    disabled={busy}
                    onClick={() => fileInput.current?.click()}
                >
                    Attach
                </Button>
            )}

            <input
                ref={fileInput}
                className={classes.hidden}
                type="file"
                multiple
                accept={acceptedAttachmentsAttribute}
                onChange={(e) => onFilesPicked(e.target.files)}
            />
        </div>
    );
}
